'use client'

import * as React from 'react'

const cn = (...classes: Array<string | undefined | false | null>) =>
  classes.filter(Boolean).join(' ')

type DropdownContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  rootRef: React.RefObject<HTMLDivElement>
}

const DropdownMenuContext = React.createContext<DropdownContextValue | null>(null)

const DropdownMenu = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    const handleClick = (event: MouseEvent) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, rootRef }}>
      <div ref={rootRef} className="relative inline-flex">
        {children}
      </div>
    </DropdownMenuContext.Provider>
  )
}

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, onClick, ...props }, ref) => {
  const ctx = React.useContext(DropdownMenuContext)
  if (!ctx) {
    throw new Error('DropdownMenuTrigger must be used within DropdownMenu')
  }
  return (
    <button
      ref={ref}
      type="button"
      className={cn('inline-flex items-center justify-center', className)}
      onClick={(event) => {
        onClick?.(event)
        ctx.setOpen(!ctx.open)
      }}
      {...props}
    />
  )
})

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger'

type DropdownMenuContentProps = React.HTMLAttributes<HTMLDivElement> & {
  align?: 'start' | 'end'
}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ className, align = 'start', ...props }, ref) => {
    const ctx = React.useContext(DropdownMenuContext)
    if (!ctx?.open) return null
    return (
      <div
        ref={ref}
        className={cn(
          'absolute z-50 mt-2 w-56 rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-2 shadow-lg',
          align === 'end' ? 'right-0' : 'left-0',
          className
        )}
        {...props}
      />
    )
  }
)

DropdownMenuContent.displayName = 'DropdownMenuContent'

const DropdownMenuLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-3 py-2 text-sm font-medium text-[var(--text-2)]', className)}
      {...props}
    />
  )
)

DropdownMenuLabel.displayName = 'DropdownMenuLabel'

const DropdownMenuSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('my-2 h-px bg-[var(--border)]', className)} {...props} />
  )
)

DropdownMenuSeparator.displayName = 'DropdownMenuSeparator'

const DropdownMenuItem = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, onClick, ...props }, ref) => {
  const ctx = React.useContext(DropdownMenuContext)
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-[var(--text-2)] transition hover:bg-[var(--surface-2)]',
        className
      )}
      onClick={(event) => {
        onClick?.(event)
        ctx?.setOpen(false)
      }}
      {...props}
    />
  )
})

DropdownMenuItem.displayName = 'DropdownMenuItem'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
}
